import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UserMediaAppFilterModel, UserMediaAppUpsertModel } from 'src/app/media-app/models';
import { FilterModel } from 'src/models';
import { Observable } from 'rxjs';
import { buildBulkDeleteByFilterPayload } from 'src/app/shared/services/bulk-delete-by-filter';

@Injectable({
    providedIn: 'root',
})
export class UserMediaAppService {

    private apiEndPoint = environment.apiBaseUrl + 'api/userMediaApp/';

    constructor(private http: HttpClient) { }

    add(model: UserMediaAppUpsertModel) {
        return this.http.post(this.apiEndPoint + 'add', model);
    }

    edit(model: UserMediaAppUpsertModel) {
        return this.http.post(this.apiEndPoint + 'edit', model);
    }

    delete(id: number) {
        return this.http.post(this.apiEndPoint + 'delete/' + id, null);
    }

    deleteBulk(ids: number[]) {
        return this.http.post(this.apiEndPoint + 'delete-bulk', ids);
    }

    deleteBulkByFilter(filterModel: any, excludedIds: number[] = []) {
        const payload = buildBulkDeleteByFilterPayload(filterModel, excludedIds);
        return this.http.post<{ deletedCount: number }>(this.apiEndPoint + 'delete-bulk-by-filter', payload);
    }

    getForEdit(id: number) {
        return this.http.get(this.apiEndPoint + 'get-for-edit/' + id);
    }

    getDuration(id: number, displayListId: number) {
        const requestUrl = `${this.apiEndPoint}get-duration?id=${id}&displayListId=${displayListId}`;
        return this.http.get(requestUrl);
    }

    /**
     * Fetches YouTube's actual mostPopular (trending) videos for a region,
     * optionally filtered by videoCategoryId. Single API call returns
     * snippet + contentDetails + statistics, so no per-video follow-ups
     * are needed.
     */
    getYoutubeTrending(regionCode: string = 'US', categoryId: string = '', pageToken: string = '') {
        const catParam = categoryId ? `&videoCategoryId=${categoryId}` : '';
        const pageParam = pageToken ? `&pageToken=${pageToken}` : '';
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/videos?key=${environment.googleApi.assessor
                }&part=snippet,contentDetails,statistics&chart=mostPopular&regionCode=${regionCode
                }${catParam}&maxResults=10${pageParam}`);
    }

    getYoutubeVideos(query: string, pageToken: string = '', eventType: string = '') {
        const eventTypeParam = eventType ? `&eventType=${eventType}` : '';
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/search?q=${query
                }&key=${environment.googleApi.assessor
                }&part=snippet&videoEmbeddable=true&type=video&maxResults=10${eventTypeParam
                }&pageToken=${pageToken}`);
    }

    getYoutubePlaylists(query: string, pageToken: string = '') {
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/search?q=${query
                }&key=${environment.googleApi.assessor
                }&part=snippet&type=playlist&maxResults=10&pageToken=${pageToken}`);
    }

    getYoutubeVideoStatistics(videoId: string) {
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/videos?key=${environment.googleApi.assessor
                }&part=statistics&id=${videoId}`);
    }

    getYoutubeVideoContentDetails(videoId: string) {
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/videos?key=${environment.googleApi.assessor
                }&part=contentDetails&id=${videoId}`);
    }

    /**
     * Converts YouTube duration format (ISO 8601) to seconds
     * Example: PT4M13S -> 253 seconds, PT1H2M3S -> 3723 seconds
     */
    convertYoutubeDurationToSeconds(duration: string): number {
        if (!duration) return 0;
        
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
        if (!match) return 0;
        
        const hours = parseInt(match[1] || '0', 10);
        const minutes = parseInt(match[2] || '0', 10);
        const seconds = parseInt(match[3] || '0', 10);
        
        return hours * 3600 + minutes * 60 + seconds;
    }

    getYoutubeChannelsSnippet(channelId: string) {
        return this.http
            .get(`${environment.googleApi.youtubeSearchBaseUrl}/channels?key=${environment.googleApi.assessor
                }&part=snippet&id=${channelId}`);
    }

    getDefaultTitle(mediaAppId: number) {
        return this.http.get(this.apiEndPoint + 'get-default-title/' + mediaAppId);
    }

    getPagedResult(params: any) {
        return this.http.get(this.apiEndPoint + 'paged-result', {params});
    }

    exchangePowerBiToken(authCode: string, codeVerifier: string): Observable<string> {
        const redirectUri = `${window.location.origin}/assets/outlook-auth-callback.html`;
        return this.http.post(this.apiEndPoint + 'powerbi/exchange-token',
            { authCode, codeVerifier, redirectUri }, { responseType: 'text' });
    }

    /** Asks the backend to check whether a Google Sheets embedUrl is renderable
     *  by an anonymous viewer. Browser can't fetch docs.google.com directly due
     *  to CORS, so we route through our own API which proxies the GET server-
     *  side. Goes through Angular's HttpClient so auth headers are attached. */
    probeSheetsEmbedViewable(embedUrl: string): Observable<{ viewable: boolean }> {
        const params = { embedUrl };
        return this.http.get<{ viewable: boolean }>(this.apiEndPoint + 'sheets/probe-embed', { params });
    }

    browsePowerBIWorkspaces(request: any): Observable<any> {
        return this.http.post(this.apiEndPoint + 'powerbi/browse/workspaces', request);
    }

    browsePowerBIContent(groupId: string, request: any): Observable<any> {
        return this.http.post(this.apiEndPoint + `powerbi/browse/workspaces/${groupId}/content`, request);
    }

    browsePowerBIPages(groupId: string, reportId: string, request: any): Observable<any> {
        return this.http.post(this.apiEndPoint + `powerbi/browse/workspaces/${groupId}/reports/${reportId}/pages`, request);
    }

    getPagedList(params: any) {
        if (!params.groupId || params.groupId === -1) {
            params.groupId = null;
        }
        return this.http.get(this.apiEndPoint + 'paged-list' , {params});
    }

    exists(filterModel: FilterModel, folderId?: number): Observable<any> {
        if (!filterModel.groupId || filterModel.groupId === -1) {
            filterModel.groupId = null;
        }
        if(folderId == undefined ){
            folderId = null
        }
        return this.http.get(`${this.apiEndPoint}exists/${filterModel.groupId}/${folderId}?mineOnly=${filterModel.mineOnly}`);
    }


    folderExists(groupId?: number, mineOnly?: boolean) {
        if (groupId == undefined || groupId == -1) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }

        return this.http.get(`${this.apiEndPoint}has-folder/${groupId}?mineOnly=${mineOnly}`);
    }

    getFolderList(groupId?: number, mineOnly?: boolean, filterKey?: string) {
        if (groupId == undefined) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }
        if (filterKey == undefined) {
            filterKey = null;
        }
        return this.http.get(`${this.apiEndPoint}folders/${groupId}?mineOnly=${mineOnly}&filterKey=${filterKey}`);
    }
    GetMediaList(groupId?: number, query = '') {
        const params = {
            name: query,
            groupId: groupId || null
        }
        if (!query) {
            delete params.name;
        }
        return this.http.get(`${this.apiEndPoint}list-items-dndesigner`, {params});
    }
}